import type { ErrorResponse } from '../../../error.ts';
import type {
    BodyChunk,
    HttpOutputMessage,
    HttpStatusCode,
    MutableHttpHeaders,
    ResponseCookie
} from '../../../../../types/web/http';
import type { ServerHttpResponse, ServerWebExchange } from '../../../../../types/web/server';
import { getContentType, MapHttpHeaders } from '../../../../http/headers.ts';
import { HttpStatus, httpStatusCode } from '../../../../http/status.ts';
import type { ServerResponse, ServerResponseContext } from './types.ts';
import type { BodyInserter, BodyInserterContext } from '../types.ts';
import { MIMEType } from 'node:util';
import { LOG_PREFIX_HINT } from '../../../../codec/hints.ts';

export async function from(response: ErrorResponse) {
    return status(response.statusCode)
        .headers(headers => {
            for (const name of response.headers.keys()) {
                headers.add(name, response.headers.list(name));
            }
        })
        .bodyValue(response.body);
}

function status(status: HttpStatusCode | number): BodyBuilder {
    status = typeof status === 'number' ? httpStatusCode(status) : status;
    return new DefaultServerResponseBuilder(status);
}

function ok() {
    return status(HttpStatus.OK);
}

function created(location: URL) {
    const builder = status(HttpStatus.CREATED);
    return builder.location(location);
}

export default {
    from,
    status,
    ok,
    created,
}

export interface HeadersBuilder {
    headers(consumer: (headers: MutableHttpHeaders) => void): this;

    location(location: URL): this;

    build(): Promise<ServerResponse>

    build(writeFunction: (exchange: ServerWebExchange, context: ServerResponseContext) => Promise<void>): Promise<ServerResponse>
}

export interface BodyBuilder extends HeadersBuilder {
    contentLength(length: number): this;
    contentType(contentType: MIMEType): this;
    bodyValue(body: unknown): Promise<ServerResponse>;
}

function writeWithMessageWriters(message: HttpOutputMessage<MutableHttpHeaders>,
                                 context: BodyInserterContext,
                                 promise: Promise<Awaited<ArrayBufferView<ArrayBuffer> | ArrayBuffer | Blob | string>>,
                                 bodyType) {
    const mediaType = getContentType(message.headers);
    for (const writer of context.messageWriters) {
        if (writer.canWrite(bodyType, mediaType)) {
            return writer.write(promise, bodyType, message, mediaType);
        }
    }
    return Promise.reject(new Error(`No HttpMessageWriter found for body type ${bodyType} and media type ${mediaType}`));
}

function bodyFromValue<T>(body: BodyChunk): BodyInserter<T> {
    return async (message: HttpOutputMessage<MutableHttpHeaders>, context: BodyInserterContext) => {
        return writeWithMessageWriters(message, context, Promise.resolve(body), typeof body);
    }
}

class DefaultServerResponseBuilder implements BodyBuilder {
    readonly #statusCode: HttpStatusCode;
    readonly #headers: MutableHttpHeaders = new MapHttpHeaders();
    readonly #cookies: Map<string, ResponseCookie[]> = new Map();
    readonly #hints: Map<string, unknown> = new Map();

    constructor(statusCode: HttpStatusCode) {
        this.#statusCode = statusCode;
    }

    header(headerName: string, ...headerValues: string[]): this {
        for (const headerValue of headerValues) {
            this.#headers.add(headerName, headerValue);
        }
        return this;
    }

    cookie(cookie: ResponseCookie): this {
        const cookies = this.#cookies.get(cookie.name) ?? [];
        cookies.push(cookie);
        this.#cookies.set(cookie.name, cookies);
        return this;
    }

    headers(consumer: (headers: MutableHttpHeaders) => void): this {
        consumer(this.#headers);
        return this;
    }

    contentType(contentType: MIMEType): this {
        this.#headers.set('Content-Type', contentType.toString());
        return this;
    }

    contentLength(length: number): this {
        this.#headers.set('Content-Length', length.toString());
        return this;
    }

    location(location: URL | undefined): this {
        this.#headers.set('Location', location?.href);
        return this;
    }

    bodyValue(body: BodyChunk): Promise<ServerResponse> {
        return this.#initEntityResponse(body, bodyFromValue(body));
    }

    #initEntityResponse<T>(entity: T, inserter: BodyInserter<T>): Promise<ServerResponse> {
        return Promise.resolve(new DefaultEntityResponse<T>(this.#statusCode, this.#headers, new Map(this.#cookies), entity, inserter, new Map(this.#hints)));
    }

    build(writeFunction?: (exchange: ServerWebExchange, context: ServerResponseContext) => Promise<void>): Promise<ServerResponse> {
        if (writeFunction) {
            return Promise.resolve(new WriterFunctionServerResponse(this.#statusCode, this.#headers, this.#cookies, writeFunction));
        }
        else {
            return this.build(async (exchange, context) => {
                await exchange.response.end();
            });
        }
    }

}

export abstract class AbstractServerResponse implements ServerResponse {
    readonly #statusCode: HttpStatusCode
    readonly #headers: MutableHttpHeaders
    readonly #cookies: Map<string, ResponseCookie[]>;

    /* internal */ readonly hints?: Map<string, unknown>;

    protected constructor(
        statusCode: HttpStatusCode,
        headers: MutableHttpHeaders,
        cookies: Map<string, ResponseCookie[]>,
        hints?: Map<string, unknown>
    ) {
        this.#statusCode = statusCode;
        this.#headers = headers;
        this.#cookies = cookies;
        this.hints = hints;
    }

    get statusCode() {
        return this.#statusCode;
    }

    get headers() {
        return this.#headers;
    }

    get cookies(): ResponseCookie[] {
        const allCookies: ResponseCookie[] = [];
        for (const cookieList of this.#cookies.values()) {
            allCookies.push(...cookieList);
        }
        return allCookies;
    }

    async writeTo(exchange: ServerWebExchange, context: ServerResponseContext): Promise<void> {
        this.#writeStatusAndHeaders(exchange.response);

        // todo check not modified

        return this.writeInternal(exchange, context);
    }

    #writeStatusAndHeaders(response: ServerHttpResponse) {
        response.setStatusCode(this.#statusCode);
        for (const name of this.#headers.keys()) {
            response.headers.add(name, this.#headers.list(name));
        }
        for (const cookieList of this.#cookies.values()) {
            for (const cookie of cookieList) {
                response.addCookie(cookie);
            }
        }
    }

    protected abstract writeInternal(exchange: ServerWebExchange, context: unknown): Promise<void>;
}

const NO_HINTS = new Map<string, unknown>();
class WriterFunctionServerResponse extends AbstractServerResponse {
    readonly #writeFunction: (exchange: ServerWebExchange, context: ServerResponseContext) => Promise<void>;

    constructor(
        statusCode: HttpStatusCode,
        headers: MutableHttpHeaders,
        cookies: Map<string, ResponseCookie[]>,
        writeFunction: (exchange: ServerWebExchange, context: ServerResponseContext) => Promise<void>
    ) {
        super(statusCode, headers, cookies);
        this.#writeFunction = writeFunction;
    }

    protected async writeInternal(exchange: ServerWebExchange, context: ServerResponseContext): Promise<void> {
        await this.#writeFunction(exchange, context);
    }
}

interface EntityResponse<T> extends ServerResponse {
    readonly entity: T;

    readonly inserter: BodyInserter<T>;

    readonly hints?: ReadonlyMap<string, unknown>;
}

class DefaultEntityResponse<T> extends AbstractServerResponse implements EntityResponse<T> {
    readonly #entity: T;
    readonly #inserter: BodyInserter<T>;

    constructor(
        statusCode: HttpStatusCode,
        headers: MutableHttpHeaders,
        cookies: Map<string, ResponseCookie[]>,
        entity: T, inserter: BodyInserter<T>,
        hints: Map<string, unknown>) {
        super(statusCode, headers, cookies, hints);
        this.#entity = entity;
        this.#inserter = inserter;
    }

    get entity(): T {
        return this.#entity;
    }

    get inserter(): BodyInserter<T> {
        return this.#inserter;
    }

    protected async writeInternal(exchange: ServerWebExchange, { messageWriters }: ServerResponseContext): Promise<void> {

        this.hints?.set(LOG_PREFIX_HINT, exchange.logPrefix);
        await this.inserter(
            exchange.response,
            {
                messageWriters,
                serverRequest: exchange.request,
                hints: this.hints
            });
    }
}
